import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, ArrowRight, Loader2, Building2 } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromBankInfo = location.state?.from === '/bank-info';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success('Welcome back!');
      if (fromBankInfo) {
        navigate('/bank-info');
      } else {
        navigate(user.role === 'admin' ? '/admin' : '/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0F] flex flex-col lg:flex-row relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-600 rounded-full filter blur-[200px] opacity-10"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-600 rounded-full filter blur-[200px] opacity-10"></div>
      </div>
      
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 relative z-10">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-block mb-6 sm:mb-8">
            <Logo />
          </Link>

          <div className="animate-fade-in-up">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
              Welcome Back
            </h1>
            <p className="text-slate-500 mb-6 sm:mb-8 text-sm sm:text-base">
              Sign in to access your application dashboard
            </p>

            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-400">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 sm:h-12 bg-[#12121A] border-slate-700 text-white placeholder:text-slate-600 focus:border-violet-500 focus:ring-violet-500/20"
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-400">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 sm:h-12 bg-[#12121A] border-slate-700 text-white placeholder:text-slate-600 pr-12 focus:border-violet-500 focus:ring-violet-500/20"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 sm:h-12 btn-gradient text-white border-0"
                data-testid="login-submit"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="ml-2" size={18} />
                  </>
                )}
              </Button>
            </form>

            <p className="mt-6 sm:mt-8 text-center text-slate-500 text-sm sm:text-base">
              Don't have an account?{' '}
              <Link to="/register" className="text-violet-400 hover:text-violet-300 font-medium">
                Join Our Team
              </Link>
            </p>

            <div className="mt-4 sm:mt-6 text-center">
              <Link to="/admin/login" className="text-slate-600 hover:text-slate-400 text-sm">
                Admin Login
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-900/80 via-[#12121A] to-cyan-900/50">
          <img
            src="https://images.pexels.com/photos/4792713/pexels-photo-4792713.jpeg"
            alt="Technician working with cables"
            className="w-full h-full object-cover opacity-40 mix-blend-overlay"
          />
        </div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white">
            {fromBankInfo ? (
              <>
                <Building2 className="text-violet-400 mx-auto mb-4" size={48} />
                <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  <span className="silver-text">Direct</span>{' '}
                  <span className="gradient-text">Deposit</span>
                </h2>
                <p className="text-lg sm:text-xl text-slate-300 max-w-md">
                  Please sign in to submit your bank information for direct deposit payments
                </p>
              </>
            ) : (
              <>
                <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  <span className="silver-text">Power Your</span>{' '}
                  <span className="gradient-text">Career</span>
                </h2>
                <p className="text-lg sm:text-xl text-slate-300 max-w-md">
                  Join our network of skilled technicians installing cable, internet, and fiber
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
